import { CustomerOption } from '@asset-solutions/customer-portal/orders/models';
import { orderTempDeviation } from '@asset-solutions/customer-portal/orders/utils';
import { createSelector } from '@ngrx/store';

import {
  selectArrivalStatus,
  selectCustomerIds,
  selectFleetIds,
  selectIsAssigned,
  selectOrders,
  selectOrdersState,
  selectPickupEndDate,
  selectPickupStartDate,
  selectTempRange,
  selectTripStatus
} from './orders.reducer';

export const selectOrdersFilters = createSelector({
  arrivalStatus: selectArrivalStatus,
  customerIds: selectCustomerIds,
  fleetIds: selectFleetIds,
  isAssigned: selectIsAssigned,
  pickupEndDate: selectPickupEndDate,
  pickupStartDate: selectPickupStartDate,
  tempRange: selectTempRange,
  tripStatus: selectTripStatus
});

export const selectCustomerOptions = createSelector(selectOrders, (orders) => {
  return orders
    .map((order) => ({
      customerId: order.shipmentDetails.customerId,
      customerName: order.shipmentDetails.customerName
    }))
    .filter(
      (order, index, orders: CustomerOption[]) =>
        orders.findIndex((comparedOrder: CustomerOption) => comparedOrder.customerId === order.customerId) === index
    );
});

export const selectOrderFilterCount = createSelector(selectOrdersFilters, (filters) => {
  return Object.keys(filters).reduce((count, key) => {
    const filter = filters[key as keyof typeof filters];
    let incrementAmount = 0;

    if (Array.isArray(filter)) incrementAmount = filter.length;
    if (typeof filter === 'string') incrementAmount = 1;

    return count + incrementAmount;
  }, 0);
});

export const selectMultipleCustomersSelected = createSelector(
  selectCustomerOptions,
  (customerOptions) => customerOptions.length > 1
);

export const selectFilteredOrders = createSelector(selectOrdersState, (state) => {
  const { arrivalStatus, customerIds, fleetIds, orders, pickupEndDate, pickupStartDate, isAssigned, tempRange } = state;

  return orders.filter((order) => {
    const tempDeviation = orderTempDeviation(order.trailer);
    const hasTempDeviation = tempDeviation.returnAir || tempDeviation.temps;
    const orderArrivalStatus =
      order.tripStatus === 'Scheduled' ? order.origin?.stopArrivalStatus : order.nextStop?.stopArrivalStatus;

    const hasArrivalStatus =
      arrivalStatus.length === 0 || (!!orderArrivalStatus && arrivalStatus.includes(orderArrivalStatus));
    const hasCustomerId = customerIds.length === 0 || customerIds.includes(order.shipmentDetails.customerId);
    const hasPlannedStatus = isAssigned.length === 0 || isAssigned.includes(order.isAssigned);
    const hasTempRange =
      tempRange.length === 0 ||
      (hasTempDeviation && tempRange.includes('OutOfRange')) ||
      (!hasTempDeviation && tempRange.includes('InRange'));
    const hasMatchingFleetId =
      fleetIds.length === 0 || (order.tractor && order.tractor.fleetId && fleetIds.includes(order.tractor.fleetId));

    const originDate = order.origin?.scheduledArrivalTime ? new Date(order.origin.scheduledArrivalTime) : undefined;

    const afterPickupStart = pickupStartDate && originDate ? new Date(pickupStartDate) < originDate : true;
    const beforePickupEnd = pickupEndDate && originDate ? new Date(pickupEndDate) > originDate : true;

    return (
      afterPickupStart &&
      beforePickupEnd &&
      hasArrivalStatus &&
      hasCustomerId &&
      hasPlannedStatus &&
      hasTempRange &&
      hasMatchingFleetId
    );
  });
});

export const selectSummaryCounts = createSelector(selectFilteredOrders, (orders) => {
  return orders.reduce(
    (summary, order) => {
      const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
      const tomorrowStart = new Date(new Date().setHours(24, 0, 0, 0));
      const originDate = order.origin?.scheduledArrivalTime ? new Date(order.origin.scheduledArrivalTime) : undefined;
      const picksUpToday = originDate && originDate >= todayStart && originDate < tomorrowStart;
      const orderArrivalStatus =
        order.tripStatus === 'Scheduled' ? order.origin?.stopArrivalStatus : order.nextStop?.stopArrivalStatus;

      if (order.tripStatus === 'InTransit') summary.inTransit++;
      if (orderArrivalStatus === 'LATE') summary.late++;
      if (order.tripStatus === 'Scheduled' && picksUpToday) summary.pickingUpToday++;

      return summary;
    },
    { inTransit: 0, late: 0, pickingUpToday: 0 }
  );
});
